import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { MarketingShell } from "@/components/marketing/MarketingShell";
import { BLOG_POSTS } from "@/data/blogPosts";

const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogPage() {
  useEffect(() => {
    document.title = "Tax Lien & Tax Sale Investing Blog | Lien Scout";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Practical guides on tax lien certificates, tax deed auctions, due diligence checklists and lien-to-value analysis — from the team behind Lien Scout."
      );
    }
    return () => {
      document.title = "Lien Scout";
    };
  }, []);

  return (
    <MarketingShell>
      <div className="max-w-6xl mx-auto px-6 py-16" data-testid="blog-page">
        <div className="max-w-2xl">
          <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-red-600">
            The Ledger — Lien Scout Blog
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-3 leading-tight">
            Tax sale investing, without the folklore.
          </h1>
          <p className="mt-4 text-zinc-700">
            Field guides on liens, deeds, due diligence and deal analysis —
            written for investors who work from the county's actual lists.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {BLOG_POSTS.map((post, i) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group bg-white border-2 border-black p-6 shadow-[4px_4px_0_#000] hover:shadow-[6px_6px_0_#000] hover:-translate-y-0.5 transition-all flex flex-col"
            >
              {post.image ? (
                <div className="-mx-6 -mt-6 mb-5 border-b-2 border-black overflow-hidden">
                  <img
                    src={post.image}
                    alt=""
                    className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest">
                <span className="bg-yellow-300 border border-black px-2 py-0.5">{post.category}</span>
                <span className="text-zinc-500">{fmtDate(post.date)}</span>
              </div>
              <h2 className="font-display font-bold text-lg mt-4 leading-snug group-hover:text-red-600">
                {post.title}
              </h2>
              <p className="mt-3 text-sm text-zinc-600 flex-1">{post.excerpt}</p>
              <div className="mt-5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 font-mono-data text-[10px] uppercase text-zinc-500">
                  <Clock size={12} /> {post.readTime}
                </span>
                <span className="inline-flex items-center gap-1 font-display font-bold text-sm text-red-600">
                  Read guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 bg-brand-black text-white border-2 border-black p-8 shadow-[6px_6px_0_#dc2626]">
          <div className="md:flex items-center justify-between gap-8">
            <div>
              <h2 className="font-display font-extrabold text-lg md:text-lg">
                Stop reading about deals. Start ranking them.
              </h2>
              <p className="mt-2 text-white/70 text-sm max-w-xl">
                Lien Scout pulls live county tax sale lists nationwide, scores every
                property with AI, and emails you when a match clears your bar. 3-day free trial.
              </p>
            </div>
            <Link
              to="/pricing"
              data-testid="blog-cta-pricing"
              className="mt-6 md:mt-0 inline-flex items-center gap-2 bg-yellow-400 text-black font-display font-bold px-6 py-3 border-2 border-black shadow-[3px_3px_0_#fff] hover:shadow-[5px_5px_0_#fff] transition-shadow whitespace-nowrap"
            >
              Start free trial <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </MarketingShell>
  );
}
